"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ChapterExperience {
    title: string;
    location: string;
    website?: string;
    role: string;
    description: string;
    skills: string[];
    footerLink?: { href: string; label: string };
}

export default function ExperienceChapters({ experiences }: { experiences: ChapterExperience[] }) {
    const [active, setActive] = useState(0);
    const total = experiences.length;

    const scrollTo = (i: number) =>
        document.getElementById(`chapter-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

    return (
        <section id="experience" data-anchor className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
            <div className="grid gap-10 lg:grid-cols-[240px_1fr]">
                {/* Sticky chapter rail (desktop) */}
                <div className="hidden lg:block">
                    <div className="sticky top-[calc(var(--header-height)+4rem)]">
                        <h2 className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-primary">
                            02 — Experience
                        </h2>
                        <ol className="border-l border-border">
                            {experiences.map((exp, i) => (
                                <li key={i}>
                                    <button
                                        type="button"
                                        onClick={() => scrollTo(i)}
                                        className="group relative block w-full py-2.5 pl-5 text-left"
                                    >
                                        <span
                                            className={cn(
                                                "absolute -left-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full transition-all duration-300",
                                                i === active
                                                    ? "scale-125 bg-primary ring-4 ring-primary/20"
                                                    : i < active
                                                      ? "bg-primary/50"
                                                      : "bg-border"
                                            )}
                                        />
                                        <span
                                            className={cn(
                                                "block text-sm font-medium transition-colors duration-300",
                                                i === active ? "text-foreground" : "text-muted-foreground"
                                            )}
                                        >
                                            {exp.title}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>

                <div>
                    <h2 className="mb-2 font-mono text-xs uppercase tracking-[0.3em] text-primary lg:hidden">
                        02 — Experience
                    </h2>
                    <div className="divide-y divide-border/60">
                        {experiences.map((exp, i) => (
                            <Chapter key={i} index={i} total={total} exp={exp} onActive={setActive} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function Chapter({
    exp,
    index,
    total,
    onActive,
}: {
    exp: ChapterExperience;
    index: number;
    total: number;
    onActive: (i: number) => void;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { margin: "-45% 0px -45% 0px" });

    useEffect(() => {
        if (inView) onActive(index);
    }, [inView, index, onActive]);

    return (
        <motion.div
            id={`chapter-${index}`}
            ref={ref}
            className="scroll-mt-24 py-12 first:pt-0 sm:py-16"
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
            <span className="font-mono text-sm tracking-widest text-primary">
                {String(index + 1).padStart(2, "0")}
                <span className="text-muted-foreground"> / {String(total).padStart(2, "0")}</span>
            </span>

            <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <h3 className="font-heading text-3xl leading-tight tracking-tight sm:text-5xl">{exp.title}</h3>
                <span className="text-sm text-muted-foreground">{exp.location}</span>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
                <Badge>{exp.role}</Badge>
                {exp.website && (
                    <Link
                        href={exp.website}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary transition-colors hover:text-foreground"
                    >
                        {exp.website.replace(/^https?:\/\//, "")}
                        <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                )}
            </div>

            <p className="mt-5 max-w-2xl leading-relaxed text-muted-foreground">{exp.description}</p>

            {exp.skills.length > 0 && (
                <div className="mt-5 flex max-w-2xl flex-wrap gap-2">
                    {exp.skills.map((skill, idx) => (
                        <Badge key={idx} variant="secondary">
                            {skill}
                        </Badge>
                    ))}
                </div>
            )}

            {exp.footerLink && (
                <Button variant="link" className="mt-3 h-auto w-fit whitespace-normal p-0 text-left" asChild>
                    <Link href={exp.footerLink.href}>
                        {exp.footerLink.label}
                        <ArrowUpRight className="ml-1 h-4 w-4" />
                    </Link>
                </Button>
            )}
        </motion.div>
    );
}
